import React, { useCallback, useContext, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Text,
  LegacyCard as Card,
  Collapsible,
  Layout,
  Link,
  ResourceItem,
  InlineStack,
  Tag,
  Thumbnail,
  Tooltip,
  ChoiceList,
} from "@shopify/polaris";

// import { ExportIcon, ImportIcon, ViewIcon, CheckCircleIcon, XCircleIcon } from "@shopify/polaris-icons";
import { ImportIcon, ViewIcon } from "@shopify/polaris-icons";

import { QuickAction, QuickReport } from "app/reports";
import { SingleColumnLayout } from "layout/private/components";

// import from lib
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import { Banner, ResourceList, Toast } from "lib/components";
import { baseHelper, errorHelper, imageHelper } from "lib/helpers";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";
import { ProductCard, ProductSubCard } from "asset/styles/globalStyle";

import { GET_ORDER_LAKE_LIST } from "app/orderLake/apollo/queries";
import { GET_VENDOR_LIST } from "app/vendors/apollo/queries";
import { ORDER_LAKE_LIST } from "app/orderLake/apollo/subscriptions";
import Popover from "lib/components/popover/popover";
import MoreFilters from "app/orderLake/modules/generic/moreFilters/moreFilter";
import listData from "app/orderLake/modules/operator/features/list/orderListData";
import SyncOrderLake from "app/orderLake/modules/operator/features/sync/syncOrderLake";
import OperatorExportOrderLake from "app/orderLake/modules/operator/features/export/index";
import LineItem from "app/orderLake/modules/operator/features/list/component/lineItem/lineItem";
import { useLocation, useNavigate } from "react-router-dom";
// import { AllReportsTab } from "app/reporting/modules/provider/tabs";

const OperatorOrderList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const {
    ACCEPTED,
    ALL,
    CLOSED,
    COLLECTED,
    CREATED_AT,
    CRITICAL,
    DESC,
    DOWN,
    FILTER_BY,
    gql,
    HIDE_CAPITAL,
    ITEMS,
    PARTIAL_COLLECT,
    PARTIAL_REFUND,
    REFUND,
    RESERVE,
    SHIPPED,
    SHOW,
    TAGGED_WITH,
    TOTAL_ORDERS,
    UP,
  } = constant;

  const { cms, currentUser } = useContext(PrivateContext);
  const {
    roles: { name },
    moneyFormat,
  } = currentUser;
  const { mainLink, filterByStatusOption } = listData(cms, currentUser);
  const queryParam = baseHelper.getQueryParams(location.search);
  const paramFilter = (queryParam.filter && [...queryParam.filter.split(",")]) || [];
  const [filterByStatus, setFilterByStatus] = useState(paramFilter || []);
  const [perPage, setPerPage] = useState(parseInt(queryParam.perPage, 10) || 10);
  const [page, setPage] = useState(parseInt(queryParam.page, 10) || 1);
  const [search, setSearch] = useState(queryParam.search || "");
  const selectedSort = queryParam.sort_name && `${queryParam.sort_name}_${queryParam.sort_order}`;
  const [sortValue, setSortValue] = useState(selectedSort || constant.CREATED_DESC);
  const [taggedWith, setTaggedWith] = useState(queryParam.list_search || "");
  const [selectedFilter, setSelectedFilter] = useState(queryParam.list_filter || "");

  const [disable, setDisable] = useState(false);
  const [isExportModal, setIsExportModal] = useState(false);
  const [isSyncModal, setIsSyncModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const [initialTrackingCompany, setInitialTrackingCompany] = useState("");
  const [initialTrackingNumber, setInitialTrackingNumber] = useState("");
  const [message, setMessage] = useState("");

  const [orderList, setOrderList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [orderCount, setOrderCount] = useState(0);
  // const [selectedTab, setSelectedTab] = useState(0);
  const [totalOrderItem, setTotalOrderItem] = useState();
  const [totalOrderVendor, setTotalOrderVendor] = useState();
  const [vendorList, setVendorList] = useState([]);
  const [active, setActive] = useState({});
  const [activePopover, setActivePopover] = useState({});
  const [expanded, setExpanded] = useState({});
  const [trackingModal, setTrackingModal] = useState({});

  const [initiateReturn, setInitiateReturn] = useState({
    showModal: false,
    selectedOrder: false,
  });

  const inputData = {
    filter: filterByStatus && filterByStatus.length ? filterByStatus : queryParam.filter,
    path: queryParam.path,
    sort_name: queryParam.sort_name || CREATED_AT,
    sort_order: queryParam.sort_order || DESC,
    sort_date: queryParam.sort_date,
    page: parseInt(queryParam.page, 10) || 1,
    perPage: parseInt(queryParam.perPage, 10) || 10,
    list_search: queryParam.list_search,
    list_filter: queryParam.list_filter,
    search: queryParam.search,
  };
  const { data: order, loading: orderLoading, error: orderError, subscribeToMore, refetch } = useQuery(
    GET_ORDER_LAKE_LIST,
    {
      variables: {
        input: inputData,
      },
    }
  );

  const { data: vendorsData, loading: vendorsLoading, error: vendorsError } = useQuery(GET_VENDOR_LIST, {
    variables: {},
  });

  useEffect(() => {
    if (vendorsData && !vendorsError) {
      const responseData = baseHelper.getResponseData(vendorsData, gql.GET_VENDOR_LIST);
      setVendorList(responseData.vendors);
    }
  }, [vendorsData, vendorsError, gql.GET_VENDOR_LIST]);

  useEffect(() => {
    setTimeout(() => {
      setMessage("");
    }, 2000);
  }, [message]);

  const linkData = [
    {
      action: () => setIsSyncModal(true),
      caption: "Import all orders from your Shopify store",
      id: "importOrder",
      primary: true,
      label: cms("section.orderList.label.importOrderFromStore"),
      icon: ImportIcon,
    },
    // {
    //   action: () => setIsExportModal(true),
    //   caption: "Export your orders",
    //   id: "exportOrder",
    //   label: cms("section.orderList.label.exportOrder"),
    //   icon: ExportIcon,
    // },
  ];

  useEffect(() => {
    if (!orderLoading && !vendorsLoading) {
      setLoading(false);
    }
  }, [orderLoading, vendorsLoading]);

  useEffect(() => {
    if (orderError) {
      setBanner({
        isOpen: true,
        status: CRITICAL,
        title: errorHelper.parse(orderError),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderError]);
  const responseError = baseHelper.getResponseError(order, gql.GET_ORDER_LAKE_LIST);
  useEffect(() => {
    if (responseError) {
      setBanner({
        isOpen: true,
        status: CRITICAL,
        title: responseError,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseError]);
  const responseData = baseHelper.getResponseData(order, gql.GET_ORDER_LAKE_LIST);
  useEffect(() => {
    if (responseData) {
      setOrderList(responseData && responseData.orderList);
      setOrderCount(responseData && responseData.count);
    }
  }, [responseData]);

  useEffect(() => {
    subscribeToMore({
      document: ORDER_LAKE_LIST,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        refetch();
        return null;
      },
    });
  }, [subscribeToMore, refetch]);

  useEffect(() => {
    if (filterByStatus && filterByStatus.length) {
      return baseHelper.setUrl(location, navigate, { filter: filterByStatus, page: 1 });
    }
    return baseHelper.setUrl(location, navigate, { filter: "", page: 1 });
  }, [filterByStatus]);

  const removeTag = useCallback(
    (tag) => () => {
      setFilterByStatus((previousTags) => previousTags.filter((previousTag) => previousTag !== tag));
    },
    []
  );

  // const setFilters = useCallback(
  //   (newFilter, select) => {
  //     const getFilter = baseHelper.setQueryParams(location, { filter: newFilter, tab: select });
  //     navigate(`${location.pathname}?${getFilter}`);
  //   },
  //   [location, navigate]
  // );

  const handleSearchChange = (searchValue) => {
    setSearch(searchValue);
    baseHelper.setUrl(location, navigate, { search: searchValue, page: 1 });
  };

  const handleSort = (selected) => {
    setSortValue(selected);
    const sort = selected.split("_");
    if (sort && sort.length === 2) {
      baseHelper.setUrl(location, navigate, { sort_name: sort[0], sort_order: sort[1] });
    }
  };
  const handleQueryClear = () => {
    setSearch("");
    baseHelper.setUrl(location, navigate, { search: "" });
  };

  const handleTaggedWithRemove = (clearFilter = false, key) => {
    const clearParams = key === constant.FILTER_BY_STATUS ? { filter: "", page: 1 } : { list_search: "", page: 1 };
    if (clearFilter) {
      clearParams.list_filter = "";
      if (key) {
        switch (key) {
          case constant.TAGGED_WITH:
            return [setTaggedWith(""), setSelectedFilter(""), baseHelper.setUrl(location, navigate, clearParams)];
          case constant.FILTER_BY_STATUS:
            return [setFilterByStatus(null), baseHelper.setUrl(location, navigate, clearParams)];
          case constant.SELECTED_FILTER:
            return [setSelectedFilter(""), baseHelper.setUrl(location, navigate, clearParams)];
          default:
            return null;
        }
      }
    }
    clearParams.list_filter = "";
    clearParams.filter = "";
    setTaggedWith("");
    setSelectedFilter("");
    setFilterByStatus(null);
    baseHelper.setUrl(location, navigate, clearParams);
    return null;
  };

  const toggleModal = (shopifyLineItemId = false) => {
    setInitiateReturn((prevState) => ({
      ...prevState,
      showModal: !prevState.showModal,
      selectedOrder: prevState.showModal ? false : shopifyLineItemId,
    }));
    setDisable(false);
  };

  // const getTabs = {
  //   0: ALL,
  //   1: PENDING,
  //   2: ON_HOLD,
  //   3: PROCESS,
  //   4: COMPLETE,
  //   5: CANCELLED,
  //   6: FAILED,
  //   7: REFUND,
  //   8: PARTIAL,
  // };
  const handleSelect = (item) => {
    setSelectedItems(item);
  };
  // const handleTabChange = (select) => {
  //   setSelectedItems([]);
  //   setSelectedTab(select);
  //   baseHelper.setUrl(location, navigate, { page: 1 });
  //   setPage(1);
  //   setFilters(getTabs[select], select);
  // };

  const pageContent = {
    label: {
      status: cms("section.orderList.label.status"),
      fulfillmentStatus: cms("section.orderList.label.fulfillmentStatus"),
    },
  };

  const handlePageChange = (value) => {
    setSelectedItems([]);
    setPage(value);
  };

  // const promotedBulkActions = [
  //   {
  //     content: "Bulk Action",
  //   },
  // ];

  // const bulkActions = [
  // {
  // content: "Bulk Accept",
  // onAction: () => bulkAction(constant.ACCEPT),
  // icon: CheckCircleIcon,
  // disabled:
  //   bulkActionButton(constant.ACCEPT) ||
  //   (getTabs[selectedTab] !== constant.PENDING.toLowerCase() &&
  //     getTabs[selectedTab] !== constant.ALL.toLowerCase() &&
  //     getTabs[selectedTab] !== constant.NEW.toLowerCase()),
  // disabled: true,
  // },
  // {
  //   content: "Bulk Reject",
  // onAction: () => setRejectModal(true),
  // icon: XCircleIcon,
  // destructive: true,
  // disabled:
  //   bulkActionButton(constant.REJECT) ||
  //   (getTabs[selectedTab] !== constant.PENDING.toLowerCase() &&
  //     getTabs[selectedTab] !== constant.ALL.toLowerCase() &&
  //     getTabs[selectedTab] !== constant.ACCEPTED.toLowerCase() &&
  //     getTabs[selectedTab] !== constant.NEW.toLowerCase()),
  //     disabled: true,
  //   },
  // ];

  const renderItem = (item) => {
    const {
      _id: id,
      actualQuantity,
      createdAt,
      fulfillmentType,
      image,
      isRefund,
      number,
      storeOrderId,
      quantity,
      storeStatus: currentOrderStatus,
      lineItems,
    } = item;

    const vendorLineItems = lineItems.filter(
      (lineItem) => lineItem.ownerId === baseHelper.mongoIdAsString(currentUser._id)
    );

    const shippingStatuses = vendorLineItems.map((lineItem) => lineItem.shippingStatus);

    const isAllShipped = shippingStatuses.every((status) => status === constant.DISPATCHED);
    const isAllProcessing = shippingStatuses.every((status) => status === constant.AWAITING_DISPATCH);
    const isAnyShipped = !!shippingStatuses.find((status) => status === constant.DISPATCHED);

    const storeStatus =
      (isAllShipped && constant.COMPLETE) ||
      (isAllProcessing && constant.AWAITING_FULFILLMENT) ||
      (!isAllShipped && !isAllProcessing && isAnyShipped && constant.PARTIALLY_SHIPPED) ||
      currentOrderStatus;

    let { fulfillmentStatus: orderFulfillmentStatus, status } = item;

    const isReserve = (status === SHIPPED || status === CLOSED) && fulfillmentType === RESERVE;

    if (isReserve) {
      status = COLLECTED;
    }
    const statusType = status === ACCEPTED;

    if (statusType) {
      status = PARTIAL_COLLECT;
    }
    if (status === constant.REJECTED) {
      orderFulfillmentStatus = REFUND;
    }

    const isPartialRefund = isRefund && orderFulfillmentStatus === REFUND && !!(actualQuantity || quantity);

    if (isPartialRefund) {
      orderFulfillmentStatus = PARTIAL_REFUND;
    }
    const size = constant.LARGE;
    const thumbnailImage = image
      ? imageHelper.resize({ url: image, type: constant.imageTypes.THUMBNAIL })
      : constant.NOIMAGESNAP;
    // eslint-disable-next-line no-unused-vars
    const thumbnail = <Thumbnail source={thumbnailImage} size={size} />;

    const option = [
      {
        content: constant.VIEW_LABEL,
        icon: ViewIcon,
        onAction: () => {
          navigate(`${mainLink.orderNumber}${id}`);
        },
      },
    ];

    const handlePopover = (newId) => {
      setSelectedItems([]);
      setActivePopover({ [id]: !activePopover[newId] });
    };

    return (
      <ResourceItem id={id} persistActions>
        <ProductCard className={`product-card ${expanded[id] ? "Polaris-ResourceItem--selected" : ""}`}>
          <div className="list-item order-lake">
            <InlineStack align="start">
              {/* {thumbnail} */}
              <div className="ellipsis">
                <Link removeUnderline onClick={() => navigate(`${mainLink.orderNumber}${id}`)}>
                  {`Order #${number || storeOrderId}`}
                </Link>
                <Text variant="bodySm" as="p">
                  <Text as="span" tone="subdued">
                    {` ${cms("section.orderList.label.createdOn")}: ${baseHelper.formatDate(createdAt)}`}
                  </Text>
                </Text>
                <div>
                  <InlineStack>
                    {isRefund && (
                      <Tooltip content={pageContent.label.status} preferredPosition="above">
                        <Badge status={baseHelper.getBadgeType(orderFulfillmentStatus)}>
                          {baseHelper.ucFirst(orderFulfillmentStatus)}
                        </Badge>
                      </Tooltip>
                    )}
                    {storeStatus && (
                      <Tooltip content={pageContent.label.status} preferredPosition="above">
                        <Badge status={baseHelper.getBadgeType((storeStatus || "").toLowerCase())}>
                          {baseHelper.ucFirst(storeStatus)}
                        </Badge>
                      </Tooltip>
                    )}
                    <div className="history-items">
                      <Button
                        variant="tertiary"
                        disclosure={active[id] ? UP : DOWN}
                        onClick={() => {
                          setActive((prev) => {
                            return {
                              ...prev,
                              [id]: !active[id],
                            };
                          });
                          setExpanded((prev) => {
                            return {
                              ...prev,
                              [id]: !expanded[id],
                            };
                          });
                        }}
                      >
                        {`${active[id] ? HIDE_CAPITAL : SHOW} ${ITEMS}`}
                      </Button>
                    </div>
                  </InlineStack>
                </div>
              </div>
              <div className="manage-button">
                <Popover
                  active={activePopover[id]}
                  setActive={() => {
                    handlePopover(id);
                  }}
                  options={option}
                />
              </div>
            </InlineStack>
          </div>
          <Collapsible
            open={active[id]}
            id={`timeline-${active[id] + 1}`}
            transition={{ duration: "150ms", timingFunction: "ease" }}
          >
            <ProductSubCard className="collapsible-card">
              <LineItem
                cms={cms}
                initialTrackingCompany={initialTrackingCompany}
                initialTrackingNumber={initialTrackingNumber}
                moneyFormat={moneyFormat}
                option={option}
                orderId={id}
                role={name}
                vendorList={vendorList}
                setBanner={setBanner}
                setInitialTrackingCompany={setInitialTrackingCompany}
                setInitialTrackingNumber={setInitialTrackingNumber}
                setTotalOrderItem={setTotalOrderItem}
                setTotalOrderVendor={setTotalOrderVendor}
                setTrackingModal={setTrackingModal}
                showHistory
                totalOrderItem={totalOrderItem}
                totalOrderVendor={totalOrderVendor}
                trackingModal={trackingModal}
                id={id}
                disable={disable}
                initiateReturn={initiateReturn}
                setDisable={setDisable}
                toggleModal={toggleModal}
              />
            </ProductSubCard>
          </Collapsible>
        </ProductCard>
      </ResourceItem>
    );
  };

  const handleClearAll = () => {
    handleTaggedWithRemove(false);
    handleQueryClear();
  };

  let tagMarkup = [];
  if (filterByStatus && filterByStatus.length) {
    tagMarkup =
      filterByStatus &&
      filterByStatus.map((option) => (
        <Tag key={option} onRemove={removeTag(option)}>
          {baseHelper.ucFirst(option)}
        </Tag>
      ));
  }

  function disambiguateLabel(key, value) {
    switch (key) {
      case constant.TAGGED_WITH:
        return `${value}`;
      case constant.SELECTED_FILTER:
        return cms(`section.orderList.label.filter.${value}`) || value === constant.STORE_ORDER_ID
          ? cms("section.orderList.label.orderId")
          : value;
      case constant.FILTER_BY_STATUS:
        return <div className="remove_tag">{tagMarkup}</div>;
      default:
        return value;
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  }

  const appliedFilterArrObj = [];
  if (!isEmpty(taggedWith)) {
    appliedFilterArrObj.push({
      key: constant.TAGGED_WITH,
      label: disambiguateLabel(constant.TAGGED_WITH, taggedWith),
      onRemove: () => handleTaggedWithRemove(true, constant.TAGGED_WITH),
    });
  }
  if (!isEmpty(selectedFilter)) {
    appliedFilterArrObj.push({
      key: constant.SELECTED_FILTER,
      label: disambiguateLabel(constant.SELECTED_FILTER, selectedFilter),
      onRemove: () => handleTaggedWithRemove(true, constant.SELECTED_FILTER),
    });
  }

  if (!isEmpty(filterByStatus)) {
    appliedFilterArrObj.push({
      key: constant.FILTER_BY_STATUS,
      label: disambiguateLabel(constant.FILTER_BY_STATUS, filterByStatus),
      onRemove: () => handleTaggedWithRemove(true, constant.FILTER_BY_STATUS),
    });
  }

  const handleAction = (value) => {
    setFilterByStatus(value);
    baseHelper.setUrl(location, navigate, { filter: value, page: 1 });
    if (!value.length) {
      baseHelper.setUrl(location, navigate, { filter: ALL, page: 1 });
    }
  };

  const appliedFilters = appliedFilterArrObj;
  const filters = [
    {
      key: TAGGED_WITH,
      // label: cms("section.orderList.label.filterBy"),
      label: FILTER_BY,
      filter: (
        <MoreFilters
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      ),
      shortcut: false,
    },
    {
      key: constant.FILTER_BY_STATUS,
      // label: cms("common.label.filterByStatus"),
      label: "Filter By Status",
      filter: (
        <ChoiceList
          title={cms("common.label.filterByStatus")}
          titleHidden
          choices={filterByStatusOption}
          selected={filterByStatus || []}
          onChange={(value) => handleAction(value)}
          allowMultiple
        />
      ),
    },
  ];

  const closeBanner = () => {
    setBanner({
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const getComponent = () => {
    return (
      <>
        {banner.isOpen && (
          <>
            <Banner
              isOpen={banner.isOpen}
              status={banner.status}
              title={banner.title}
              onDismiss={() => closeBanner()}
            />
            <br />
          </>
        )}
        <QuickReport />
        <QuickAction link={linkData} />
        <Layout>
          <OperatorExportOrderLake isOpenModal={isExportModal} setIsOpenModal={setIsExportModal} />
          <SyncOrderLake
            cms={cms}
            isSyncOpen={isSyncModal}
            setIsSyncModal={setIsSyncModal}
            setBanner={setBanner}
            refetch={refetch}
          />
          <Layout.Section>
            <Card>
              {/* <Tabs tabs={listData(cms).tabsData} selected={selectedTab} onSelect={handleTabChange} /> */}
              <div className="order-list">
                <ResourceList
                  items={orderList}
                  renderItem={renderItem}
                  idForItem={(orders) => {
                    const { _id: id } = orders;
                    return id;
                  }}
                  loading={loading || orderLoading}
                  queryValue={search}
                  onQueryClear={handleQueryClear}
                  onQueryChange={handleSearchChange}
                  sortOptions={listData(cms).sortOptions}
                  sortValue={sortValue}
                  onSortChange={handleSort}
                  selectedItems={selectedItems}
                  // bulkActions={bulkActions}
                  // promotedBulkActions={promotedBulkActions}
                  onSelectionChange={handleSelect}
                  handleClearAll={handleClearAll}
                  count={orderCount}
                  page={page}
                  perPage={perPage}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  setPage={(value) => handlePageChange(value)}
                  setPerPage={setPerPage}
                  totalCountLabel={TOTAL_ORDERS}
                />
              </div>
            </Card>
            <Toast message={message} />
          </Layout.Section>
        </Layout>
      </>
    );
  };
  // <TwoColumnLayout primary={getComponent()} secondary={getSecondaryComponent() />
  return <SingleColumnLayout primary={getComponent()} />;
};

export default withFeature(withErrorBoundary(OperatorOrderList), { feature: constant.ORDERS });
